/**
 * DiagonalCheck — 4-corner rectangle squareness inspection.
 *
 * The pattern mirrors LevelCheck: session list → session view. In the
 * session view the left column shows an SVG plan of the four corners
 * with the diagonals drawn on top and the delta highlighted; the right
 * column is a numbered take-shot rail — one big button per corner
 * slot (A, B, C, D) that fires station.measure and posts the result
 * to /api/diagonal/sessions/:id/shots with `index` = corner slot.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Plus, Crosshair, Trash, Download, Camera, Printer, RotateCcw, Copy } from "lucide-react";
import { api, station } from "../lib/api";
import { ToolSplitLayout } from "../components/ToolSplitLayout";
import { AimPad } from "../components/AimPad";
import { DiagonalPhotoOverlay } from "../components/DiagonalPhotoOverlay";
import DiagonalGuided from "./DiagonalGuided";

const CORNER_NAMES = ["A", "B", "C", "D"];

const diagApi = {
  list: () => api.get("/diagonal/sessions").then((r) => r.data),
  create: (b) => api.post("/diagonal/sessions", b).then((r) => r.data),
  get: (id) => api.get(`/diagonal/sessions/${id}`).then((r) => r.data),
  del: (id) => api.delete(`/diagonal/sessions/${id}`).then((r) => r.data),
  addShot: (id, b) => api.post(`/diagonal/sessions/${id}/shots`, b).then((r) => r.data),
  delShot: (id, sid) => api.delete(`/diagonal/sessions/${id}/shots/${sid}`).then((r) => r.data),
  setTol: (id, tolerance_mm) => api.patch(`/diagonal/sessions/${id}/tolerance`, { tolerance_mm }).then((r) => r.data),
};

const Stat = ({ label, value, tone }) => (
  <div className={`rounded-sm border p-2 ${
    tone === "fail" ? "border-[#FF3B30]/40 bg-[#FF3B30]/10"
    : tone === "ok" ? "border-[#8DC63F]/40 bg-[#8DC63F]/10"
    : "border-[#2A2E39] bg-[#0A0B0E]"
  }`}>
    <div className="text-[9px] uppercase tracking-widest text-[#5A6065]">{label}</div>
    <div
      className={`mt-0.5 font-mono text-lg ${
        tone === "fail" ? "text-[#FF3B30]" : tone === "ok" ? "text-[#8DC63F]" : "text-white"
      }`}
    >
      {value}
    </div>
  </div>
);

/**
 * Plan-view SVG of the 4 corners + diagonals. The two diagonals are
 * drawn in different colours; the longer one is bright, the shorter
 * one dashed so the surveyor can immediately see which way the shape
 * is skewed.
 */
const PlanView = ({ shots }) => {
  const sorted = [...shots].sort((a, b) => a.index - b.index);
  if (sorted.length < 4) {
    return (
      <div className="mx-auto flex aspect-square w-full max-w-[520px] items-center justify-center text-xs text-[#5A6065]">
        Shoot all 4 corners to see the plan view.
      </div>
    );
  }
  const [a, b, c, d] = sorted;
  // Rotate the shape so its LONGEST side sits horizontal on screen.
  // The total station measures corners in its own local frame — a
  // real rectangle appears tilted at whatever heading it happened to
  // be set up at, and if we naively pinned A→B horizontal a wide
  // rectangle whose long edge is B→C would still read as a tall
  // sliver. Picking the longest side keeps the shape as wide as
  // possible in the viewport. Rotation preserves distances so all
  // reported diagonals / side lengths / angles are unaffected.
  const sides = [
    { p: a, q: b },
    { p: b, q: c },
    { p: c, q: d },
    { p: d, q: a },
  ].map((s) => ({ ...s, len: Math.hypot(s.q.x - s.p.x, s.q.y - s.p.y) }));
  const longest = sides.reduce((best, s) => (s.len > best.len ? s : best), sides[0]);
  const theta = Math.atan2(longest.q.y - longest.p.y, longest.q.x - longest.p.x);
  const cosT = Math.cos(-theta);
  const sinT = Math.sin(-theta);
  const originX = longest.p.x;
  const originY = longest.p.y;
  const rot = (p) => ({
    x: (p.x - originX) * cosT - (p.y - originY) * sinT,
    y: (p.x - originX) * sinT + (p.y - originY) * cosT,
    id: p.id,
    z: p.z,
  });
  const rSorted = sorted.map(rot);
  const [ra, rb, rc, rd] = rSorted;
  const xs = rSorted.map((p) => p.x);
  const ys = rSorted.map((p) => p.y);
  const zs = [a.z, b.z, c.z, d.z];
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const w = maxX - minX || 1;
  const h = maxY - minY || 1;
  // Square viewport. Fit the shape into the same viewport for both
  // axes so its true proportions read on screen (a square shape looks
  // square, a long rectangle looks long) — the previous 480×320 canvas
  // stretched the shape into a landscape rectangle no matter what.
  const svgSize = 520;
  // Reserve room on all 4 sides for the elevation chip (84px wide,
  // centered 38px outside the corner dot along the centroid→corner
  // vector — up to 80px past the corner). Without this margin the
  // right-hand chip on a wide rectangle gets clipped by the viewport.
  const pad = 90;
  const scale = Math.min((svgSize - 2 * pad) / w, (svgSize - 2 * pad) / h);
  // Centre the fitted shape inside the square viewport.
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const tx = (x) => svgSize / 2 + (x - cx) * scale;
  // SVG y is inverted so higher survey-Y appears higher on screen.
  const ty = (y) => svgSize / 2 - (y - cy) * scale;

  const ac = Math.hypot(rc.x - ra.x, rc.y - ra.y);
  const bd = Math.hypot(rd.x - rb.x, rd.y - rb.y);
  const acLonger = ac >= bd;

  // Elevation deviation from mean — one of the two headline numbers
  // per corner along with the plan-XY position. `dz_mm` is signed:
  // positive = corner is above the mean plane, negative = below.
  const zMean = (zs[0] + zs[1] + zs[2] + zs[3]) / 4;
  const zDevs = zs.map((z) => (z - zMean) * 1000);
  const zDevAbsMax = Math.max(...zDevs.map(Math.abs));

  // Where should the elevation chip sit relative to the corner dot?
  // Put it on the side facing OUTWARDS from the shape's centre so
  // the label never falls inside the outline (which would clash with
  // the diagonal lines running through the middle).
  const chipOffset = (px, py) => {
    const vx = px - cx;
    const vy = py - cy;
    const norm = Math.hypot(vx, vy) || 1;
    // 38px outwards along the centroid→corner vector — leaves room
    // for the corner-name label on the inside and the chip on the
    // outside without collision.
    return { dx: (vx / norm) * 38, dy: -(vy / norm) * 38 };
  };

  return (
    <svg
      viewBox={`0 0 ${svgSize} ${svgSize}`}
      className="mx-auto block aspect-square w-full max-w-[520px]"
    >
      {/* Shape outline */}
      <polygon
        points={rSorted.map((p) => `${tx(p.x)},${ty(p.y)}`).join(" ")}
        fill="none" stroke="#009E8E" strokeWidth={2}
      />
      {/* Diagonal AC */}
      <line
        x1={tx(ra.x)} y1={ty(ra.y)} x2={tx(rc.x)} y2={ty(rc.y)}
        stroke={acLonger ? "#F5A623" : "#4A5054"}
        strokeWidth={acLonger ? 2 : 1.5}
        strokeDasharray={acLonger ? "" : "6 4"}
      />
      <text x={(tx(ra.x) + tx(rc.x)) / 2} y={(ty(ra.y) + ty(rc.y)) / 2 - 6}
            fontSize={11} fontFamily="JetBrains Mono, monospace"
            fill={acLonger ? "#F5A623" : "#8B939A"} textAnchor="middle">
        {`A-C ${(ac * 1000).toFixed(0)} mm`}
      </text>
      {/* Diagonal BD */}
      <line
        x1={tx(rb.x)} y1={ty(rb.y)} x2={tx(rd.x)} y2={ty(rd.y)}
        stroke={!acLonger ? "#F5A623" : "#4A5054"}
        strokeWidth={!acLonger ? 2 : 1.5}
        strokeDasharray={!acLonger ? "" : "6 4"}
      />
      <text x={(tx(rb.x) + tx(rd.x)) / 2} y={(ty(rb.y) + ty(rd.y)) / 2 + 14}
            fontSize={11} fontFamily="JetBrains Mono, monospace"
            fill={!acLonger ? "#F5A623" : "#8B939A"} textAnchor="middle">
        {`B-D ${(bd * 1000).toFixed(0)} mm`}
      </text>
      {/* Corner dots + name label + elevation chip */}
      {rSorted.map((p, i) => {
        const px = tx(p.x); const py = ty(p.y);
        const { dx, dy } = chipOffset(px, py);
        const dz = zDevs[i];
        const sign = dz > 0 ? "+" : dz < 0 ? "" : "±"; // Math.abs is negative sign already
        const chipFill = Math.abs(dz) < 0.5
          ? "#8DC63F"                                   // dead flat
          : dz > 0 ? "#F5A623" : "#3AB8FF";             // high vs low
        const isMax = Math.abs(dz) === zDevAbsMax && zDevAbsMax > 0.5;
        return (
          <g key={p.id}>
            <circle cx={px} cy={py} r={6} fill="#8DC63F" />
            {/* Corner name — placed on the opposite side to the chip
                so the two don't clash. */}
            <text
              x={px - dx * 0.8} y={py - dy * 0.8 + 5}
              fontSize={15} fontFamily="Chakra Petch, sans-serif" fontWeight={700}
              fill="#8DC63F" textAnchor="middle"
            >
              {CORNER_NAMES[i]}
            </text>
            {/* Elevation-deviation chip */}
            <g transform={`translate(${px + dx}, ${py + dy})`}>
              <rect x={-42} y={-14} width={84} height={28} rx={4}
                    fill="#0A0B0E" stroke={chipFill}
                    strokeWidth={isMax ? 2 : 1} />
              <text x={0} y={6}
                    fontSize={16} fontFamily="JetBrains Mono, monospace"
                    fontWeight={700} fill={chipFill} textAnchor="middle">
                {`${sign}${dz.toFixed(1)} mm`}
              </text>
            </g>
          </g>
        );
      })}
      {/* Mean-elevation legend, bottom-left */}
      <g transform={`translate(12, ${svgSize - 14})`}>
        <text fontSize={12} fontFamily="Chakra Petch, sans-serif"
              fill="#8B939A" letterSpacing="0.1em" fontWeight={700}>
          {`MEAN Z ${zMean.toFixed(3)} m · ±${zDevAbsMax.toFixed(1)} mm max`}
        </text>
      </g>
    </svg>
  );
};

const CornerRail = ({ shots, nextIdx, onTake, onDelete, disabled }) => (
  <div className="grid grid-cols-4 gap-2">
    {CORNER_NAMES.map((name, idx) => {
      const shot = shots.find((s) => s.index === idx);
      const isNext = idx === nextIdx;
      return (
        <button
          key={name}
          data-testid={`diag-corner-${name}`}
          disabled={disabled || (!isNext && !shot)}
          onClick={() => (shot ? onDelete(shot.id) : onTake(idx))}
          className={`flex flex-col items-center justify-center rounded-md border p-3 text-center transition-all disabled:cursor-not-allowed disabled:opacity-40 ${
            shot
              ? "border-[#8DC63F] bg-[#8DC63F]/10 text-[#8DC63F] hover:border-[#FF3B30] hover:bg-[#FF3B30]/10 hover:text-[#FF3B30]"
              : isNext
              ? "animate-pulse border-[#F5A623] bg-[#F5A623]/10 text-[#F5A623]"
              : "border-[#2A2E39] bg-[#0A0B0E] text-[#5A6065]"
          }`}
          style={{ fontFamily: "Chakra Petch, sans-serif" }}
        >
          <span className="text-[9px] uppercase tracking-widest">Corner</span>
          <span className="text-2xl font-black">{name}</span>
          <span className="text-[9px] uppercase tracking-widest">
            {shot ? "tap to clear" : isNext ? "next" : "—"}
          </span>
        </button>
      );
    })}
  </div>
);

export default function DiagonalCheck({ onBack }) {
  const [sessions, setSessions] = useState([]);
  const [current, setCurrent] = useState(null);
  const [newName, setNewName] = useState("");
  const [newTol, setNewTol] = useState("3.0");
  const [measuring, setMeasuring] = useState(false);
  // Guided mode auto-enables when a fresh session is created and stays
  // on until the surveyor exits it. When re-opening an existing session
  // from the list we default to the classic view so shots stay editable.
  const [guided, setGuided] = useState(false);
  // Photo capture — kept per-session, ephemeral (data URL, not persisted
  // yet). Pins are 4 × {u,v} in 0..1 space of the photo. Reset whenever
  // the current session changes so we don't leak session A's photo
  // onto session B.
  const [photoDataUrl, setPhotoDataUrl] = useState(null);
  const [photoPins, setPhotoPins] = useState([null, null, null, null]);
  useEffect(() => {
    setPhotoDataUrl(null);
    setPhotoPins([null, null, null, null]);
  }, [current?.id]);

  const refreshList = useCallback(async () => {
    try { setSessions(await diagApi.list()); } catch { /* silent */ }
  }, []);
  useEffect(() => { refreshList(); }, [refreshList]);

  const refreshCurrent = useCallback(async (sid) => {
    try {
      const s = await diagApi.get(sid);
      setCurrent(s);
      // Route based on completion:
      //  - Complete session (all 4 corners recorded) → jump straight
      //    to the RESULTS view. From there the surveyor gets the delta
      //    + PASS/FAIL, the tap-to-redo corner tiles, and the "New
      //    survey of same job" button to start a fresh run without
      //    hunting for the create-session dialog.
      //  - Incomplete session → open guided mode so they can keep
      //    shooting where they left off.
      // Previously we ALWAYS opened guided mode even for complete
      // sessions, which meant re-opening a finished job dropped the
      // surveyor into the auto-shoot state machine (weird UX + risk
      // of a phantom shot after a re-lock). Fixed per surveyor field
      // feedback.
      const isComplete = (s?.shots?.length || 0) >= 4;
      setGuided(!isComplete);
    } catch { toast.error("Session load failed"); }
  }, []);

  const createSession = async () => {
    const name = newName.trim();
    if (!name) return toast.error("Give the session a name");
    const tol = parseFloat(newTol) || 3.0;
    try {
      const s = await diagApi.create({ name, tolerance_mm: tol });
      setNewName("");
      await refreshList();
      setCurrent(s);
      setGuided(true);          // freshly created → jump straight into guided
      toast.success(`Session "${name}" ready`);
    } catch { toast.error("Could not create session"); }
  };

  const deleteSession = async (id) => {
    if (!window.confirm("Delete this diagonal session?")) return;
    try { await diagApi.del(id); await refreshList(); } catch { toast.error("Delete failed"); }
  };

  const nextIdx = useMemo(() => {
    if (!current) return 0;
    for (let i = 0; i < 4; i++) if (!current.shots.some((s) => s.index === i)) return i;
    return 4;
  }, [current]);

  const takeShot = async (idx) => {
    if (!current) return;
    setMeasuring(true);
    try {
      const m = await station.measure();
      if (!Number.isFinite(m?.x) || !Number.isFinite(m?.y) || !Number.isFinite(m?.z)) {
        throw new Error("no coord");
      }
      const s = await diagApi.addShot(current.id, {
        index: idx,
        label: CORNER_NAMES[idx],
        x: m.x, y: m.y, z: m.z,
        hz: m.hz, v: m.v, sd: m.sd,
      });
      setCurrent(s);
      toast.success(`Corner ${CORNER_NAMES[idx]} recorded`);
    } catch (e) {
      toast.error(e?.message === "no coord" ? "Measurement failed — aim at target" : "Shot failed");
    } finally { setMeasuring(false); }
  };

  const deleteShot = async (shotId) => {
    if (!current) return;
    try {
      const s = await diagApi.delShot(current.id, shotId);
      setCurrent(s);
    } catch { toast.error("Delete failed"); }
  };

  // ---- Session list view ------------------------------------------
  if (!current) {
    return (
      <div className="min-h-screen bg-[#0A0B0E] text-white" data-testid="diagonal-page">
        <div className="mx-auto max-w-4xl px-4 py-4">
          <div className="mb-4 flex items-center justify-end">
            <div className="text-[10px] uppercase tracking-widest text-[#8B939A]">
              4-corner squareness · plate / slab / frame
            </div>
          </div>

          <div className="mb-6 rounded-md border border-[#2A2E39] bg-gradient-to-br from-[#1A1D24] to-[#14161A] p-4">
            <div className="mb-3 text-[10px] uppercase tracking-widest text-[#8B939A]">New session</div>
            <div className="grid gap-3 sm:grid-cols-[1fr,120px,140px]">
              <input
                data-testid="diag-new-name" value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Feature ID (e.g. Base plate A)"
                className="rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-3 py-2 font-mono text-sm text-white placeholder:text-[#4A5054] focus:border-[#009E8E] focus:outline-none"
              />
              <div className="flex items-center gap-2">
                <input
                  data-testid="diag-new-tol" type="number" step="0.1"
                  value={newTol} onChange={(e) => setNewTol(e.target.value)}
                  className="w-full rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-3 py-2 font-mono text-sm text-white focus:border-[#009E8E] focus:outline-none"
                />
                <span className="text-[10px] uppercase tracking-widest text-[#8B939A]">± mm</span>
              </div>
              <button
                data-testid="diag-create-btn" onClick={createSession}
                className="flex items-center justify-center gap-2 rounded-sm border border-[#009E8E] bg-[#009E8E]/10 px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-[#009E8E] hover:bg-[#009E8E]/20"
                style={{ fontFamily: "Chakra Petch, sans-serif" }}
              >
                <Plus size={12} strokeWidth={2.5} /> Create
              </button>
            </div>
            <div className="mt-2 text-[10px] text-[#5A6065]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
              Shoot A → B → C → D going round the shape. Tool reports Δdiagonal (mm), sides, and each corner&apos;s angle.
            </div>
          </div>

          <div className="space-y-2">
            {sessions.length === 0 ? (
              <div className="rounded-md border border-dashed border-[#2A2E39] bg-[#14161A] p-8 text-center text-xs text-[#5A6065]">
                No sessions yet.
              </div>
            ) : (
              sessions.map((s) => (
                <div
                  key={s.id}
                  className="flex items-center justify-between rounded-md border border-[#2A2E39] bg-[#14161A] p-3 hover:border-[#009E8E]"
                >
                  <button
                    className="flex-1 text-left"
                    onClick={() => refreshCurrent(s.id)}
                    data-testid={`diag-open-${s.id}`}
                  >
                    <div className="font-bold text-white" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
                      {s.name}
                    </div>
                    <div className="mt-1 text-[10px] text-[#8B939A]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                      {s.n_shots}/4 corners · ±{s.tolerance_mm.toFixed(1)} mm
                      {s.delta_mm != null && (
                        <> · Δ {s.delta_mm.toFixed(1)} mm{" "}
                          <span className={s.within_tol ? "text-[#8DC63F]" : "text-[#FF3B30]"}>
                            {s.within_tol ? "PASS" : "FAIL"}
                          </span>
                        </>
                      )}
                    </div>
                  </button>
                  <button
                    onClick={() => deleteSession(s.id)}
                    className="rounded-sm border border-transparent p-1.5 text-[#5A6065] hover:border-[#FF3B30] hover:text-[#FF3B30]"
                    data-testid={`diag-del-${s.id}`}
                  >
                    <Trash size={14} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---- Routing ----------------------------------------------------
  // Route on `guided`, not on shot count. The user is the ONLY thing
  // that flips guided off (via the "View results" button in guided's
  // COMPLETE phase, or the Exit button on an incomplete session). This
  // means landing the 4th shot no longer yanks the surveyor out of the
  // shooting flow — they see the COMPLETE banner (with delta + PASS/
  // FAIL and tap-to-redo corner tiles) and choose whether to view
  // results or re-shoot a corner.
  if (guided) {
    return (
      <DiagonalGuided
        current={current}
        api={api}
        setCurrent={setCurrent}
        onExit={() => {
          // From guided COMPLETE → results view (stay on session).
          // From guided incomplete → back to session list.
          if (current.shots.length >= 4) setGuided(false);
          else { setGuided(false); setCurrent(null); }
        }}
      />
    );
  }

  // ---- Results view (complete session) ---------------------------
  // Plan + stats + elevation chips + side/angle table, no right rail.
  return (
    <ToolSplitLayout
      data-testid="diagonal-session"
      title={current.name}
      subtitle={
        current.delta_mm == null
          ? `${4 - current.shots.length} more corners to shoot`
          : `Δdiag ${current.delta_mm.toFixed(1)} mm — ${current.within_tol ? "PASS" : "FAIL"}`
      }
      left={
        <div className="space-y-4">
          {/* Print stylesheet — light theme, hide chrome, force photo
              overlay + summary onto A4. Mirrors OvalityCheck pattern. */}
          <style>{`
            @media print {
              @page { size: A4; margin: 14mm 12mm 20mm 12mm; }
              html, body { background: #ffffff !important; color: #0A0B0E !important; }
              .no-print { display: none !important; }
              [data-testid="tool-split-right"], header { display: none !important; }
              .print-only { display: block !important; }
              table { color: #0A0B0E !important; }
              table thead tr { background: #E5E7EB !important; }
            }
            .print-only { display: none; }
          `}</style>
          {/* Back to session list — the FabAxis logo in the header
              goes to the toolbox; this button is the one-tap way
              back to *this tool's* session list without leaving the
              Diagonal Check screen. */}
          <div className="flex items-center justify-between gap-2 no-print">
            <button
              type="button"
              data-testid="diag-results-back"
              onClick={() => setCurrent(null)}
              className="flex items-center gap-2 rounded-md border border-[#2A2E39] bg-[#14161A] px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-[#B5BEC5] hover:border-[#009E8E] hover:text-white"
              style={{ fontFamily: "Chakra Petch, sans-serif" }}
            >
              <ArrowLeft size={14} strokeWidth={2.5} />
              Back to sessions
            </button>
            {/* Reshoot the whole survey — resume this same session in
                guided mode so the surveyor can tap a corner tile and
                re-shoot individual corners while keeping the original
                job identity + tolerance. */}
            <button
              type="button"
              data-testid="diag-results-reshoot"
              onClick={() => setGuided(true)}
              className="flex items-center gap-2 rounded-md border border-[#F5A623]/40 bg-[#F5A623]/10 px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-[#F5A623] hover:bg-[#F5A623]/20"
              style={{ fontFamily: "Chakra Petch, sans-serif" }}
              title="Re-open this survey in guided mode to re-shoot one or more corners"
            >
              <RotateCcw size={14} strokeWidth={2.5} />
              Reshoot corners
            </button>
            {/* Second survey of same job — clones the name (+ "· run 2"
                / "· run 3" etc) and tolerance into a fresh session so
                the surveyor can survey the same flange twice for a
                repeatability check without hunting the create-session
                dialog. Falls back gracefully if `create` fails. */}
            <button
              type="button"
              data-testid="diag-results-new-run"
              onClick={async () => {
                try {
                  // Count how many sessions already share this base name
                  // (strip any existing "· run N" suffix) so run numbering
                  // is stable even after re-openings.
                  const baseName = (current.name || "Diagonal").replace(/\s*·\s*run\s*\d+\s*$/i, "");
                  const runN = (sessions || []).filter(
                    (x) => (x.name || "").replace(/\s*·\s*run\s*\d+\s*$/i, "") === baseName
                  ).length + 1;
                  const s = await diagApi.create({
                    name: `${baseName} · run ${runN}`,
                    tolerance_mm: current.tolerance_mm ?? 3.0,
                  });
                  await refreshList();
                  setCurrent(s);
                  setGuided(true);
                  toast.success(`Second survey started — "${s.name}"`);
                } catch { toast.error("Could not start second survey"); }
              }}
              className="flex items-center gap-2 rounded-md border border-[#00E1C7]/40 bg-[#00E1C7]/10 px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-[#00E1C7] hover:bg-[#00E1C7]/20"
              style={{ fontFamily: "Chakra Petch, sans-serif" }}
              title="Start a second survey of the same job (same name + tolerance, fresh shots)"
            >
              <Copy size={14} strokeWidth={2.5} />
              New survey · same job
            </button>
            <div className="flex items-center gap-2">
              {current.delta_mm != null && (
                <>
                  <label
                    data-testid="diag-photo-input-label"
                    className="flex cursor-pointer items-center gap-2 rounded-md border border-[#00E1C7]/40 bg-[#00E1C7]/10 px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-[#00E1C7] hover:bg-[#00E1C7]/20"
                    style={{ fontFamily: "Chakra Petch, sans-serif" }}
                  >
                    <Camera size={14} strokeWidth={2.5} />
                    {photoDataUrl ? "Retake photo" : "Add photo"}
                    <input
                      data-testid="diag-photo-input"
                      type="file" accept="image/*" capture="environment" className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        const rdr = new FileReader();
                        rdr.onload = () => { setPhotoDataUrl(rdr.result); setPhotoPins([null, null, null, null]); };
                        rdr.readAsDataURL(f);
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    data-testid="diag-print-btn"
                    onClick={() => window.print()}
                    className="flex items-center gap-2 rounded-md border border-[#8DC63F]/40 bg-[#8DC63F]/10 px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-[#8DC63F] hover:bg-[#8DC63F]/20"
                    style={{ fontFamily: "Chakra Petch, sans-serif" }}
                  >
                    <Printer size={14} strokeWidth={2.5} />
                    Print report
                  </button>
                </>
              )}
              <div className="text-right text-[10px] uppercase tracking-widest text-[#8B939A]"
                   style={{ fontFamily: "Chakra Petch, sans-serif" }}>
                <div className="text-white">{current.name}</div>
                {current.delta_mm != null && (
                  <div className={current.within_tol ? "text-[#8DC63F]" : "text-[#FF3B30]"}>
                    Δdiag {current.delta_mm.toFixed(1)} mm — {current.within_tol ? "PASS" : "FAIL"}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Field photo with the surveyor's measured geometry
              overlaid on top. Data-URL only — nothing persisted
              server-side yet; the print report captures whatever
              is on screen. */}
          {current.delta_mm != null && photoDataUrl && (
            <div className="rounded-md border border-[#2A2E39] bg-[#14161A] p-3">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-[9px] uppercase tracking-[0.3em] text-[#8B939A] no-print"
                     style={{ fontFamily: "Chakra Petch, sans-serif" }}>
                  Field photo
                </div>
                <div className="text-[9px] uppercase tracking-widest text-[#8B939A] no-print">
                  {photoPins.filter(Boolean).length} / 4 corners placed
                </div>
              </div>
              <DiagonalPhotoOverlay
                photoDataUrl={photoDataUrl}
                pins={photoPins}
                onPinsChange={setPhotoPins}
                shots={current.shots}
                deltaMm={current.delta_mm}
                withinTol={current.within_tol}
                mode="capture"
              />
            </div>
          )}

          {/* Summary card — only when 4 shots are in */}
          {current.delta_mm != null && (
            <div className="grid grid-cols-2 gap-3 rounded-md border border-[#009E8E]/30 bg-[#009E8E]/5 p-3 md:grid-cols-4">
              <Stat label="Diagonal A-C" value={`${(current.diagonal_ac_m * 1000).toFixed(1)} mm`} />
              <Stat label="Diagonal B-D" value={`${(current.diagonal_bd_m * 1000).toFixed(1)} mm`} />
              <Stat
                label="Δ diagonal"
                value={`${current.delta_mm.toFixed(1)} mm`}
                tone={current.within_tol ? "ok" : "fail"}
              />
              <Stat
                label="Worst angle"
                value={`${Math.max(...current.corner_angles_deg.map((a) => Math.abs(a - 90))).toFixed(2)}° off`}
              />
            </div>
          )}

          <PlanView shots={current.shots} />

          {/* Detail table */}
          {current.delta_mm != null && (
            <div className="overflow-hidden rounded-md border border-[#2A2E39] bg-[#14161A]">
              <table className="w-full text-[11px]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                <thead>
                  <tr className="border-b border-[#2A2E39] bg-[#0A0B0E] text-[#8B939A]">
                    <th className="p-2 text-left uppercase tracking-widest">Side</th>
                    <th className="p-2 text-right uppercase tracking-widest">Length (mm)</th>
                    <th className="p-2 text-left uppercase tracking-widest">Corner</th>
                    <th className="p-2 text-right uppercase tracking-widest">Angle</th>
                    <th className="p-2 text-right uppercase tracking-widest">Δ from 90°</th>
                  </tr>
                </thead>
                <tbody className="text-[#B5BEC5]">
                  {["AB", "BC", "CD", "DA"].map((sn, i) => {
                    const ang = current.corner_angles_deg[i];
                    const off = Math.abs(ang - 90);
                    return (
                      <tr key={sn} className="border-b border-[#1A1D24]">
                        <td className="p-2 text-white">{sn}</td>
                        <td className="p-2 text-right">{(current.side_lengths_m[i] * 1000).toFixed(1)}</td>
                        <td className="p-2 text-white">{CORNER_NAMES[i]}</td>
                        <td className="p-2 text-right">{ang.toFixed(2)}°</td>
                        <td className={`p-2 text-right ${off > 0.5 ? "text-[#FF3B30]" : "text-white"}`}>
                          {off.toFixed(2)}°
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      }
      right={null}
    />
  );
}
